import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import GuestFilledIcon from "../assets/icons/guest_filled.svg";
import GuestEmptyIcon from "../assets/icons/guest_empty.svg";
import GuestPlaceholderBanner from "./GuestPlaceholderBanner";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { useTranslation } from "react-i18next";

interface GuestCardProps {
    firstName: string | null;
    lastName: string | null;
    completed: boolean;
    index: number;
    totalGuests: number;
    isChild?: boolean;
}

const GuestCard: React.FC<GuestCardProps> = ({
    firstName,
    lastName,
    completed,
    index,
    totalGuests,
    isChild,
}) => {
    const { colors: BrandColors, isDark } = useBrandAppearance();
    const { navigateWithLang } = useNavigateWithLang();
    const { t } = useTranslation("check_in");

    const cleanedFirst = (firstName ?? "").trim();
    const cleanedLast = (lastName ?? "").trim();
    const hasAnyName = !!(cleanedFirst || cleanedLast);
    const displayName = [cleanedFirst, cleanedLast].filter(Boolean).join(" ");

    const GuestIconSrc = completed ? GuestFilledIcon : GuestEmptyIcon;

    const handleCardClick = () => {
        navigateWithLang(`/guest-details/${index}`);
    };

    return (
        <Box
            onClick={handleCardClick}
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                maxWidth: 340,
                minHeight: 160,
                height: 220,
                backgroundColor: "var(--oc-paper-bg)",
                borderRadius: 2,
                boxShadow: "0px 4px 12px rgba(0,0,0,0.3)",
                position: "relative",
                padding: 0,
                overflow: "hidden",
                cursor: "pointer",
                transition: "transform 0.2s ease-in-out",
                "&:hover": { transform: "scale(1.02)" },
            }}
        >
            {/* Show placeholder if no name */}
            {!hasAnyName && <GuestPlaceholderBanner />}

            {/* Name + status */}
            {hasAnyName && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        p: 2,
                    }}
                >
                    <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: "var(--oc-text)",
                                textAlign: "left",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {displayName}
                        </Typography>

                        {!completed && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "var(--oc-text-muted)",
                                    textAlign: "left",
                                    mt: 0.5,
                                    fontStyle: "italic",
                                }}
                            >
                                {t("pleaseFillGuestData", "Please fill in guest data...")}
                            </Typography>
                        )}
                    </Box>

                    {completed && (
                        <CheckIcon sx={{ color: "#4CAF50", fontSize: "2rem", ml: 1 }} />
                    )}
                </Box>
            )}

            {/* Bottom Right: Guest Counter */}
            <Typography
                sx={{
                    position: "absolute",
                    bottom: 10,
                    right: 10,
                    paddingRight: "3px",
                    fontWeight: 400,
                    fontSize: "1.5rem",
                    color: "var(--oc-text)",
                }}
            >
                {index}/{totalGuests}
            </Typography>

            {/* Centered Guest SVG Icon */}
            <Box
                component="img"
                src={GuestIconSrc}
                alt="Guest Icon"
                sx={{
                    position: "absolute",
                    bottom: 0,
                    left: "50%",
                    color: BrandColors.accent,
                    transform: "translateX(-50%)",
                    width: isChild ? "100px" : "140px",
                    height: isChild ? "100px" : "140px",
                    // SVGs use dark browns; invert to light on dark UI
                    ...(isDark
                        ? {
                              filter: "brightness(0) invert(1)",
                              opacity: completed ? 0.95 : 0.55,
                          }
                        : {}),
                }}
            />
        </Box>
    );
};

export default GuestCard;
